'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { usePrizeModal } from './usePrizeModal';

export type PrizeModalQueueItem = {
  id: string;
  prizeLabel: string;
  prizeValue: number;
  isBigGain: boolean;
};

type EnqueueOptions = {
  capitalizer?: string | null;
  onDrained?: () => void;
};

type UsePrizeModalQueueReturn = {
  enqueuePrizes: (items: PrizeModalQueueItem[], options?: EnqueueOptions) => void;
  isDraining: boolean;
  clearQueue: () => void;
};

export const usePrizeModalQueue = (): UsePrizeModalQueueReturn => {
  const { showPrizeModal } = usePrizeModal();

  const [isDraining, setIsDraining] = useState(false);
  const queueRef = useRef<PrizeModalQueueItem[]>([]);
  const totalRef = useRef(0);
  const shownRef = useRef(0);
  const capitalizerRef = useRef<string | null | undefined>(undefined);
  const onDrainedRef = useRef<(() => void) | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const showNext = useCallback(() => {
    if (!isMountedRef.current) return;

    const next = queueRef.current.shift();

    if (!next) {
      totalRef.current = 0;
      shownRef.current = 0;
      setIsDraining(false);
      const onDrained = onDrainedRef.current;
      onDrainedRef.current = null;
      onDrained?.();
      return;
    }

    shownRef.current += 1;

    showPrizeModal({
      queuePosition: { current: shownRef.current, total: totalRef.current },
      prizeLabel: next.prizeLabel,
      isBigGain: next.isBigGain,
      showFireworks: next.isBigGain,
      hardClose: true,
      capitalizer: capitalizerRef.current,
      onModalClose: () => {
        timeoutRef.current = setTimeout(showNext, 250);
      },
    });
  }, [showPrizeModal]);

  const enqueuePrizes = useCallback(
    (items: PrizeModalQueueItem[], options?: EnqueueOptions) => {
      const seen = new Set<string>();
      const deduped = items.filter((item) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });

      queueRef.current = [...deduped].sort((a, b) => b.prizeValue - a.prizeValue);
      totalRef.current = deduped.length;
      shownRef.current = 0;
      capitalizerRef.current = options?.capitalizer;
      onDrainedRef.current = options?.onDrained ?? null;

      setIsDraining(queueRef.current.length > 0);
      showNext();
    },
    [showNext],
  );

  const clearQueue = useCallback(() => {
    queueRef.current = [];
    totalRef.current = 0;
    shownRef.current = 0;
    onDrainedRef.current = null;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsDraining(false);
  }, []);

  return { enqueuePrizes, isDraining, clearQueue };
};
